import React, { useEffect, useRef, useState } from "react";
import { Share2, Download, X, Loader2, Copy, MessageCircle, Check } from "lucide-react";
import { toast } from "sonner";

/* -------- Card image generator (Canvas) -------- */
const NAVY = "#0D1B2A";
const NAVY_LIGHT = "#16263A";
const GOLD = "#C9A961";
const PARCHMENT = "#F4ECDC";
const SIZE = 1080;

const drawCornerOrnament = (ctx, x, y, size, color) => {
  ctx.save(); ctx.translate(x, y);
  ctx.strokeStyle = color; ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, 0); ctx.lineTo(size, 0);
  ctx.moveTo(0, 0); ctx.lineTo(0, size);
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.beginPath(); ctx.arc(0, 0, 4, 0, Math.PI * 2); ctx.fill();
  ctx.restore();
};

const buildInviteCanvas = async () => {
  if (typeof document !== "undefined" && document.fonts && document.fonts.ready) {
    try { await document.fonts.ready; } catch { /* noop */ }
  }
  const canvas = document.createElement("canvas");
  canvas.width = SIZE; canvas.height = SIZE;
  const ctx = canvas.getContext("2d");

  // Background
  const grad = ctx.createLinearGradient(0, 0, SIZE, SIZE);
  grad.addColorStop(0, NAVY); grad.addColorStop(1, NAVY_LIGHT);
  ctx.fillStyle = grad; ctx.fillRect(0, 0, SIZE, SIZE);

  // Glow
  const flare = ctx.createRadialGradient(SIZE / 2, 380, 80, SIZE / 2, 380, 720);
  flare.addColorStop(0, "rgba(201, 169, 97, 0.30)");
  flare.addColorStop(1, "rgba(201, 169, 97, 0)");
  ctx.fillStyle = flare; ctx.fillRect(0, 0, SIZE, SIZE);

  // Frame + corners
  ctx.strokeStyle = "rgba(201, 169, 97, 0.55)";
  ctx.lineWidth = 1.5; ctx.strokeRect(40, 40, SIZE - 80, SIZE - 80);
  drawCornerOrnament(ctx, 60, 60, 28, GOLD);
  ctx.save(); ctx.translate(SIZE - 60, 60); ctx.rotate(Math.PI / 2);
  drawCornerOrnament(ctx, 0, 0, 28, GOLD); ctx.restore();
  ctx.save(); ctx.translate(SIZE - 60, SIZE - 60); ctx.rotate(Math.PI);
  drawCornerOrnament(ctx, 0, 0, 28, GOLD); ctx.restore();
  ctx.save(); ctx.translate(60, SIZE - 60); ctx.rotate(-Math.PI / 2);
  drawCornerOrnament(ctx, 0, 0, 28, GOLD); ctx.restore();

  // Brand
  ctx.font = '500 22px "Manrope", system-ui, sans-serif';
  ctx.fillStyle = GOLD; ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.fillText("BÍBLIA COM PROPÓSITO", SIZE / 2, 140);
  ctx.strokeStyle = GOLD; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(SIZE / 2 - 30, 170); ctx.lineTo(SIZE / 2 + 30, 170); ctx.stroke();

  // Big book emoji-style accent (we draw an open-book glyph)
  ctx.font = '180px "Apple Color Emoji", "Segoe UI Emoji", sans-serif';
  ctx.fillText("📖", SIZE / 2, 350);

  // Headline
  ctx.font = '600 78px "Fraunces", serif';
  ctx.fillStyle = PARCHMENT;
  ctx.fillText("Vamos juntos", SIZE / 2, 540);
  ctx.font = '600 78px "Fraunces", serif';
  ctx.fillText("nessa caminhada?", SIZE / 2, 625);

  // Subline
  ctx.font = '500 32px "Manrope", system-ui, sans-serif';
  ctx.fillStyle = "rgba(244, 236, 220, 0.85)";
  ctx.fillText("Bíblia, devocional e Auxiliar Bíblico com IA", SIZE / 2, 730);
  ctx.font = '500 28px "Manrope", system-ui, sans-serif';
  ctx.fillStyle = "rgba(244, 236, 220, 0.72)";
  ctx.fillText(", grátis, todos os dias.", SIZE / 2, 778);

  // CTA pill
  const pillW = 460, pillH = 68;
  const pillX = (SIZE - pillW) / 2; const pillY = 850;
  ctx.fillStyle = GOLD;
  ctx.beginPath();
  const r = pillH / 2;
  ctx.moveTo(pillX + r, pillY);
  ctx.lineTo(pillX + pillW - r, pillY);
  ctx.quadraticCurveTo(pillX + pillW, pillY, pillX + pillW, pillY + r);
  ctx.quadraticCurveTo(pillX + pillW, pillY + pillH, pillX + pillW - r, pillY + pillH);
  ctx.lineTo(pillX + r, pillY + pillH);
  ctx.quadraticCurveTo(pillX, pillY + pillH, pillX, pillY + r);
  ctx.quadraticCurveTo(pillX, pillY, pillX + r, pillY);
  ctx.closePath(); ctx.fill();
  ctx.fillStyle = NAVY;
  ctx.font = '600 22px "Manrope", system-ui, sans-serif';
  ctx.fillText("APP.CEMEDEIROS.COM.BR", SIZE / 2, pillY + pillH / 2);

  // Handle
  ctx.font = '500 20px "Manrope", system-ui, sans-serif';
  ctx.fillStyle = "rgba(201, 169, 97, 0.75)";
  ctx.fillText("@ccarlosmedeiross", SIZE / 2, 970);

  return canvas;
};

const buildInviteBlob = async () => {
  const canvas = await buildInviteCanvas();
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("toBlob failed"))),
      "image/jpeg", 0.92,
    );
  });
};

/* ----------------------------- Modal ----------------------------- */
const APP_URL = "https://app.cemedeiros.com.br";
const INVITE_TEXT =
  "Tô seguindo essa caminhada pela Palavra todos os dias, vem comigo? Bíblia, devocional e Auxiliar Bíblico com IA, grátis: " + APP_URL;

export default function ShareAppModal({ open, onClose }) {
  const containerRef = useRef(null);
  const [building, setBuilding] = useState(false);
  const [acting, setActing] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setBuilding(true);
    (async () => {
      try {
        const canvas = await buildInviteCanvas();
        if (cancelled) return;
        canvas.style.width = "100%";
        canvas.style.height = "auto";
        canvas.style.display = "block";
        canvas.style.borderRadius = "16px";
        const el = containerRef.current;
        if (el) { el.innerHTML = ""; el.appendChild(canvas); }
      } finally {
        if (!cancelled) setBuilding(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  if (!open) return null;

  const triggerDownload = (blob) => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "biblia-com-proposito-convite.jpg";
    document.body.appendChild(a); a.click();
    setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
  };

  const handleShare = async () => {
    if (acting) return;
    setActing(true);
    try {
      const blob = await buildInviteBlob();
      const file = new File([blob], "biblia-com-proposito-convite.jpg", { type: "image/jpeg" });
      const payload = { title: "Bíblia com Propósito", text: INVITE_TEXT, url: APP_URL, files: [file] };
      if (
        typeof navigator !== "undefined" &&
        typeof navigator.canShare === "function" &&
        navigator.canShare({ files: [file] }) &&
        typeof navigator.share === "function"
      ) {
        await navigator.share(payload);
        toast.success("Compartilhado!");
        onClose?.();
      } else {
        triggerDownload(blob);
        toast.success("Imagem baixada, anexe nos Stories.");
      }
    } catch (err) {
      if (err && err.name === "AbortError") return;
      toast.error("Não foi possível compartilhar.");
    } finally {
      setActing(false);
    }
  };

  const handleDownload = async () => {
    if (acting) return;
    setActing(true);
    try {
      const blob = await buildInviteBlob();
      triggerDownload(blob);
      toast.success("Imagem baixada!");
    } catch {
      toast.error("Não foi possível gerar a imagem.");
    } finally {
      setActing(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(INVITE_TEXT);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  const whatsappHref =
    `https://wa.me/?text=${encodeURIComponent(INVITE_TEXT)}`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="share-app-modal"
    >
      <div
        className="bg-white rounded-3xl max-w-md w-full max-h-[92vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Indicar para um amigo</p>
            <p className="text-sm text-navy/65 mt-0.5">Multiplique a Palavra</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="share-app-close"
            aria-label="Fechar"
            className="w-9 h-9 rounded-full text-navy/55 hover:bg-[hsl(var(--parchment))] flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-5 space-y-4">
          <div
            ref={containerRef}
            data-testid="share-app-preview"
            className="aspect-square w-full bg-navy rounded-2xl flex items-center justify-center text-white/60 text-sm"
          >
            {building && (
              <div className="inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Gerando imagem…
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2.5">
            <button
              type="button"
              onClick={handleShare}
              disabled={building || acting}
              data-testid="share-app-share"
              className="inline-flex items-center justify-center gap-2 bg-navy text-white hover:bg-gold hover:text-navy disabled:opacity-50 px-5 py-3 rounded-full text-[11px] uppercase tracking-widest transition-colors"
            >
              {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
              Compartilhar
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={building || acting}
              data-testid="share-app-download"
              className="inline-flex items-center justify-center gap-2 bg-white border border-border text-navy hover:border-gold/60 disabled:opacity-50 px-5 py-3 rounded-full text-[11px] uppercase tracking-widest transition-colors"
            >
              <Download className="w-4 h-4" />
              Baixar JPG
            </button>
          </div>

          <a
            href={whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="share-app-whatsapp"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] text-white hover:opacity-90 px-5 py-3 rounded-full text-[11px] uppercase tracking-widest transition-opacity"
          >
            <MessageCircle className="w-4 h-4" />
            Enviar pelo WhatsApp
          </a>

          <button
            type="button"
            onClick={handleCopyLink}
            data-testid="share-app-copy-link"
            className="w-full inline-flex items-center justify-center gap-2 text-[11px] uppercase tracking-widest text-navy/65 hover:text-gold py-2 transition-colors"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? "Copiado!" : "Copiar link e mensagem"}
          </button>

          <p className="text-[11px] text-navy/45 text-center leading-relaxed pt-1">
            Compartilhe com quem você ama. A graça nunca para.
          </p>
        </div>
      </div>
    </div>
  );
}
